import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import ErrorMessage from '@components/Common/ErrorMessage';
import type { Column } from '@components/Common/SearchFilter/SearchFilter';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { companyList } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { buildFilterArray, graphQLFilterArrayFormatter } from '@helpers/utils';
import BusinessIcon from '@mui/icons-material/Business';
import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './index.scss';
import { useSelector } from 'react-redux';
import { canWriteJobsAndCompanies, isSuperAdmin } from '@components/Common/Utils/roles';
import CompanyRequestsList from './CompanyRequestsList';
import { formatDate } from '@helpers/date';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const COMPANY_COLUMNS = ['companyName', 'approvalStatus', 'location', 'createdAt'];

const rowsPerPage = 100;

export interface CompanyList {
  id: string;
  address: {
    locality: string;
    country: string;
    city: string;
    state: string;
    zipCode: string;
    address: string;
  };
  companyName: string;
  approvalStatus: string;
  createdAt?: string;
}

const renderLocation = (row: any) => {
  const { locality, city, state, country, address } = row.address;
  const parts = [locality, city, state, country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : (address || '-');
};

const Companies = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  /*
   * Which tab is in `?tab=`, not in a route param: `/companies/:tab` would shadow
   * `/companies/:companyId`. It is in the URL at all because an admin looking at a request wants
   * to be able to paste the list to whoever asked, and this page already syncs page and dates
   * through search params.
   */
  /*
   * The requests tab is Super-Admin only, and it is hidden rather than disabled: approving a claim
   * attaches an employer to a company and can move every job they own, so the operations behind it
   * are on the BFF's Super-Admin list and an HR opening the tab would get a 403 and an empty table.
   * A tab that always fails is worse than no tab.
   */
  const role = useSelector((state: any) => state.loginUserProfile.profile?.role);
  const canSeeRequests = isSuperAdmin(role);
  // Marketing sees every company but adds none (owner's brief, 2026-10-19).
  const canAddCompany = canWriteJobsAndCompanies(role);

  /*
   * The tab is the ROUTE, not a query param.
   *
   * `/companies?tab=requests` made a distinct view -- its own table, its own columns, its own
   * page title -- look like a filter on the companies list. A query param is for narrowing what
   * a page shows; a different table with different columns is a different page. It also meant
   * the requests view could not be linked, bookmarked or given a title of its own without
   * carrying the companies route along with it.
   *
   * `/companies?tab=requests` still works: it redirects below, because those links exist in
   * people's history and in whatever they have pasted to each other.
   */
  const navigate = useNavigate();
  const location = useLocation();
  const tab = canSeeRequests && location.pathname.startsWith('/company-requests') ? 1 : 0;
  const setTab = useCallback(
    (next: number) => {
      navigate(next === 1 ? '/company-requests' : '/companies');
    },
    [navigate],
  );

  useEffect(() => {
    if (searchParams.get('tab') === 'requests' && !location.pathname.startsWith('/company-requests')) {
      const n = new URLSearchParams(searchParams);
      n.delete('tab');
      const qs = n.toString();
      navigate(`/company-requests${qs ? `?${qs}` : ''}`, { replace: true });
    }
  }, [searchParams, location.pathname, navigate]);
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const [totalElements, setTotalElements] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CompanyList[]>([]);
  const [filterData, setFilterData] = useState<Column[]>(() => {
    const initial: Column[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_') && value.trim() !== '') {
        const col = key.replace('filter_', '');
        initial.push({ id: initial.length + 1, columnName: col, searchValue: decodeURIComponent(value) });
      }
    });
    return initial;
  });
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('date_from') ?? '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('date_to') ?? '');


  const companyColMapping: Record<string, string | string[]> = {
    'Company Name': 'companyName',
    'Approval Status': 'status',
    Locality: 'companyDetails.locality',
    City: 'companyDetails.city',
    State: 'companyDetails.state',
    Country: 'companyDetails.country',
  };

  const filterColumns = Object.keys(companyColMapping);
  const skipResetOnFirstFilterChange = useRef(true);

  const onApplyFilters = useCallback(() => {
    if (skipResetOnFirstFilterChange.current) {
      skipResetOnFirstFilterChange.current = false;
      return;
    }
    if (page !== 0) setPage(0);
  }, [page]);

  const handleChangePage = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
      const newSearch = new URLSearchParams(searchParams);
      newSearch.set('page', String(newPage));
      if (newSearch.toString() !== searchParams.toString()) setSearchParams(newSearch);
    },
    [searchParams, setSearchParams],
  );

  const fetchData = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      const filterArr = buildFilterArray(filterData, companyColMapping);
      if (dateFrom) filterArr.push({ key: 'createdAtFrom', value: dateFrom, operator: 'AND' });
      if (dateTo) filterArr.push({ key: 'createdAtTo', value: dateTo, operator: 'AND' });
      const filter = graphQLFilterArrayFormatter(filterArr);
      try {
        const query = companyList(page, rowsPerPage, filter);
        const { data } = await fetchGraphQLApiData(query);
        const { getAllCompanies } = data;
        if (getAllCompanies.content) {
          const companies: CompanyList[] = getAllCompanies.content.map((item: any) => ({
            id: item._id,
            address: {
              locality: item.companyDetails?.locality ?? '',
              country: item.companyDetails?.country ?? '',
              city: item.companyDetails?.city ?? '',
              state: item.companyDetails?.state ?? '',
              zipCode: item.companyDetails?.zipCode ?? '',
              address: item.companyDetails?.address ?? '',
            },
            companyName: item.companyName,
            approvalStatus: item.status ?? '',
            createdAt: item.createdAt || '',
          }));
          setData(companies);
          setTotalElements(getAllCompanies.totalElements);
        } else {
          throw new Error('Unexpected response format');
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setLoading(false);
      }
    },
    [filterData, dateFrom, dateTo],
  );

  useEffect(() => {
    // Not while the requests tab is open. The company query is 100 rows and nothing on screen
    // shows them, so it is a wasted round trip on every filter or page change made before the
    // admin switched tabs.
    if (tab === 0) fetchData(page);
  }, [fetchData, page, filterData, tab]);

  useEffect(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    const safe = Number.isFinite(p) && p >= 0 ? p : 0;
    if (safe !== page) setPage(safe);
    setDateFrom(searchParams.get('date_from') ?? '');
    setDateTo(searchParams.get('date_to') ?? '');
  }, [searchParams.toString()]);

  const skipDateUrlSyncOnMount = useRef(true);
  useEffect(() => {
    if (skipDateUrlSyncOnMount.current) {
      skipDateUrlSyncOnMount.current = false;
      return;
    }
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      if (dateFrom) n.set('date_from', dateFrom); else n.delete('date_from');
      if (dateTo) n.set('date_to', dateTo); else n.delete('date_to');
      return n;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  // Opening a company means routing to its workspace. The list used to also
  // render the old modal inline, which is why a click produced both.
  const handleOpenPopup = (companyId: string) => {
    navigate(`/companies/${companyId}`);
  };

  return (
    /*
     * The tabs are the header's: icon, H1 and count first, the tab bar directly under them, both
     * full width. They used to sit above the H1 (and before that inside the table's column, two
     * thirds of the way across); the header owns them now so every tabbed page has one order.
     */
    <Box className="companies-root">
      <Box className="companies-panel">
    <CRMPageTemplate
      tabs={canSeeRequests
        ? { value: tab, onChange: setTab, tabs: [{ label: 'Companies' }, { label: 'Access requests' }] }
        : undefined}
      addNewLabel="Add Company"
      /* All four statuses the workspace can set. 'declined' was missing, and it is the one that
         hides a company from every public query — so the status an admin most needs to review
         was the only one they could not list. */
      columnOptions={tab === 0
        ? { 'Approval Status': ['pending', 'active', 'rejected', 'declined'] }
        : undefined}
      /* The panel filters companies by name, status and location. None of those are columns on a
         requests table, so offering it there would be a filter that silently does nothing. */
      filterColumns={tab === 0 ? filterColumns : undefined}
      locationColumns={['City', 'State', 'Country']}
      localityColumns={['Locality']}
      localityAutoFillColumns={{ Locality: { city: 'City', state: 'State', country: 'Country' } }}
      filterLabel="Filter"
      icon={<BusinessIcon />}
      initialColumns={filterData}
      setFilterData={setFilterData}
      title="Companies"
      totalCount={tab === 0 ? totalElements : undefined}
      /* "Add Company" means nothing on a list of requests: a request is created by an employer
         claiming a company, never by an admin here. */
      onAddNew={tab === 0 && canAddCompany ? () => navigate('/companies/new') : undefined}
      onApplyFilters={onApplyFilters}
      dateFrom={tab === 0 ? dateFrom : undefined}
      dateTo={tab === 0 ? dateTo : undefined}
      onDateFromChange={tab === 0 ? (v => setDateFrom(v)) : undefined}
      onDateToChange={tab === 0 ? (v => setDateTo(v)) : undefined}
    >
      {tab === 1 ? (
        <CompanyRequestsList />
      ) : loading ? (
        <SkeletonLoader rows={20} columns={COMPANY_COLUMNS.length} />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : data.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
          <Typography color="text.secondary" variant="body2">No companies found.</Typography>
        </Box>
      ) : (
        <MintlyTable
          pagination={{
            enabled: true,
            page,
            rowsPerPage,
            totalNumberOfRows: totalElements,
            handleChangePage,
          }}
          rows={data}
          selectors={COMPANY_COLUMNS}
          tableBodyRow={{
            companyName: (row: any) => <>{row.companyName}</>,
            approvalStatus: (row: any) => <CRMStatusChip status={row.approvalStatus} />,
            location: (row: any) => <>{renderLocation(row)}</>,
            createdAt: (row: any) => <>{formatDate(row.createdAt)}</>,
          }}
          tableHeadRow={{
            companyName: <>Company Name</>,
            approvalStatus: <>Status</>,
            location: <>Location</>,
            createdAt: <>Created</>,
          }}
          onRowClick={(row) => handleOpenPopup(row.id)}
        />
      )}
    </CRMPageTemplate>
      </Box>
    </Box>
  );
};

export default Companies;
